import React, { useState, useEffect } from "react";
import classes from './PaymentReceipt.module.css';
import HeaderNav from '../../../Components/Dahboard Header/HeaderNav/HeaderNav';
import Footer from "../../../Components/Footer/Footer";
import { useLocation, useNavigate } from "react-router-dom";
import api, { BASE_URL } from "../../../Components/API/Api";
import {
    BarChart2,
    Filter,
    CalendarClock,
    Mail,
    ArrowLeft,
    Printer,
    Download,
    Settings
} from 'lucide-react';

const PaymentReceipt = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const token = localStorage.getItem("authToken");
    const [ledgerData, setLedgerData] = useState([]);
    const [loading, setLoading] = useState(true);

    // new: store logged-in company info
    const [companyInfo, setCompanyInfo] = useState({
        name: "",
        address: "",
        email: "",
        phone: "",
        logo: ""
    });

    // NEW: base currency code & symbol (set from company settings)
    const [baseCurrencyCode, setBaseCurrencyCode] = useState(null);
    const [baseCurrencySymbol, setBaseCurrencySymbol] = useState(null);

    const getTypeClass = (type) => {
        switch (type.toLowerCase()) {
            case "assets":
                return "asset";
            case "liabilities":
                return "liability";
            case "equity":
                return "equity";
            case "revenue":
                return "revenue";
            case "expenses":
                return "expense";
            default:
                return "";
        }
    };


    const typeMap = {
        "1": "Assets",
        "2": "Liabilities",
        "3": "Equity",
        "4": "Revenue",
        "5": "Expenses"
    };

    const categoryMap = {
        "1": "Current Assets",
        "2": "Non-current Assets",
        "3": "Current Liabilities",
        "4": "Non-current Liabilities",
        "5": "Equity",
        "6": "Operating Revenue",
        "7": "Cost of Sales",
        "8": "Operating Expenses"
    };


    const formatMoney = (value) => {
        const num = Number(value || 0);
        return num.toLocaleString("en-NG", { minimumFractionDigits: 2 });
    };

    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const response = await api.get(`/v1/chart_of_account`);

                const accounts = Array.isArray(response.data.data?.data) ? response.data.data.data : [];
                const mappedData = accounts.map((account, index) => ({
                    id: account.id || `account-${index}`,
                    ref: account.gl_code || `REF-${index}`,
                    description: account.gl_name,
                    type: typeMap[account.class_id] || "Unknown",
                    category: categoryMap[account.category_id] || "Unknown",
                    balance: formatMoney(account.balance || 0),
                    status: account.status === "1" ? "Active" : "Inactive",
                }));


                setLedgerData(mappedData);
            } catch (error) {
                console.error("Error fetching chart of accounts:", error);
            } finally {
                setLoading(false);
            }
        };
        if (token) fetchAccounts();
        else setLoading(false);
    }, [token]);

    // new: fetch company info for header display
    useEffect(() => {
        const fetchCompanyInfo = async () => {
            if (!token) return;
            try {
                const res = await api.get(`/v1/companies/get-info`);
                const item = Array.isArray(res?.data?.data) ? res.data.data[0] : (res?.data?.data || null);
                if (item) {
                    setCompanyInfo({
                        name: item.name || "",
                        address: item.address || "",
                        email: item.email || "",
                        phone: item.phone_number || "",
                        logo: item.logo || ""
                    });

                    // NEW: resolve base currency from company info if present
                    const currencyId = item?.currency_base;
                    if (currencyId) {
                        try {
                            const currenciesRes = await api.get(`/v1/fetch-all-currencies`);
                            const currencies = Array.isArray(currenciesRes?.data?.data) ? currenciesRes.data.data : (currenciesRes?.data?.data || []);
                            const baseCurrency = currencies.find(c => String(c.id) === String(currencyId));
                            if (baseCurrency) {
                                setBaseCurrencyCode(baseCurrency.code || null);
                                setBaseCurrencySymbol(baseCurrency.symbol || null);
                            }
                        } catch (curErr) {
                            console.error("Failed to fetch currencies for base currency", curErr);
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to fetch company info", err);
            }
        };
        fetchCompanyInfo();
    }, [token]);

    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
    };

    const handleMail = () => alert("Mail clicked!");
    const handlePrinter = () => window.print();
    const handleDownload = () => {
        const csvContent = [
            ["Ref", "Description", "Type", "Category", "Balance", "Status"],
            ...ledgerData.map(d => [d.ref, d.description, d.type, d.category, d.balance, d.status])
        ].map(e => e.join(",")).join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "chart_of_accounts.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const handleSettings = () => navigate("/settings/chart-ledger");

    // NEW: read payment passed via navigate state
    const payment = location.state?.payment || null;

    // helper to format date safely
    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        if (isNaN(d)) return dateStr;
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    // helper to format money using existing formatMoney
    const formatCurrency = (value) => {
        return formatMoney(value);
    };

    // derive receipt-related values from payment with fallbacks
    const receiptNo = payment?.receipt_number || payment?.invoice_number || payment?.uuid || "N/A";
    const receiptDate = payment?.transaction_date || payment?.date_of_invoice || payment?.created_at || null;
    const receivedFrom = payment?.particular || payment?.supplier?.name || payment?.supplier || "N/A";
    const paymentMethod = payment?.mode?.name || (payment?.payment_mode
        ? (String(payment.payment_mode) === "1" ? "Bank Transfer" : String(payment.payment_mode) === "2" ? "Cheque" : `Method ${payment.payment_mode}`)
        : "N/A");
    const referenceNo = payment?.invoice_number || payment?.uuid || "N/A";

    // Use base currency (from company) first, then payment currency, then defaults
    const displayCurrencyCode = baseCurrencyCode || payment?.currency?.code || "NGN";
    const displayCurrencySymbol = baseCurrencySymbol || payment?.currency?.symbol || "₦";

    const lineAmountValue = parseFloat(payment?.total_currency_amount ?? payment?.currency_amount ?? payment?.amount ?? 0);
    const subtotalValue = lineAmountValue;
    const taxValue = 0; // API doesn't provide tax per receipt in sample; keep 0 unless you want to compute
    const totalValue = parseFloat(payment?.total_converted_amount ?? payment?.total_currency_amount ?? subtotalValue);

    // NEW: convert numbers to words (handles integer + two-decimal fractional part)
    const integerToWords = (n) => {
        if (n === 0) return "zero";
        const units = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine",
            "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen",
            "seventeen", "eighteen", "nineteen"];
        const tens = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
        const scales = [
            { value: 1e12, name: "trillion" },
            { value: 1e9, name: "billion" },
            { value: 1e6, name: "million" },
            { value: 1e3, name: "thousand" },
            { value: 1e2, name: "hundred" }
        ];

        const words = [];

        const helper = (num) => {
            let str = "";
            if (num < 20) return units[num];
            if (num < 100) {
                const t = Math.floor(num / 10);
                const u = num % 10;
                return tens[t] + (u ? "-" + units[u] : "");
            }
            for (let i = 0; i < scales.length; i++) {
                const { value, name } = scales[i];
                if (num >= value) {
                    const count = Math.floor(num / value);
                    const remainder = num % value;
                    if (value === 100) {
                        str += helper(count) + " " + name;
                    } else {
                        str += helper(count) + " " + name;
                    }
                    if (remainder) str += " " + helper(remainder);
                    return str;
                }
            }
            return str;
        };

        return helper(n).trim();
    };

    const convertAmountToWords = (amount, currencyCode) => {
        const absAmount = Math.abs(Number(amount) || 0);
        const integerPart = Math.floor(absAmount);
        const fractionPart = Math.round((absAmount - integerPart) * 100); // two decimals

        // currency names and fractional unit names map (add more as needed)
        const currencyNameMap = {
            NGN: { name: "Naira", fraction: "kobo" },
            USD: { name: "Dollar", fraction: "cents" },
            EUR: { name: "Euro", fraction: "cents" },
            GBP: { name: "Pound", fraction: "pence" }
        };
        const currencyInfo = currencyNameMap[currencyCode] || { name: currencyCode || "Units", fraction: "cents" };

        const intWords = integerToWords(integerPart);
        let words = "";

        if (integerPart === 0 && fractionPart === 0) {
            words = `Zero ${currencyInfo.name}`;
        } else if (fractionPart === 0) {
            words = `${intWords} ${currencyInfo.name}`;
        } else if (integerPart === 0) {
            const fracWords = integerToWords(fractionPart);
            words = `${fracWords} ${currencyInfo.fraction} ${currencyInfo.name.startsWith(currencyCode) ? "" : ""}`.trim();
            // prefer "X kobo" when no integer part
        } else {
            const fracWords = integerToWords(fractionPart);
            words = `${intWords} ${currencyInfo.name} and ${fracWords} ${currencyInfo.fraction}`;
        }

        // Capitalize first letter and append "Only"
        return words.replace(/^\w/, c => c.toUpperCase()) + " Only";
    };

    const amountInWords = convertAmountToWords(totalValue, displayCurrencyCode);

    return (
        <div>
            <div className={`${classes.noPrint}`}>
                <HeaderNav activePage={pageMap[location.pathname]} />
            </div>

            <div className={classes.Container}>
                {/* Action Buttons */}
                <div className={classes.actions}>
                    <button className={classes.btns} onClick={() => navigate("/transactions/receivables/sales")}>
                        New Sales Invoice
                    </button>
                    <button className={classes.btns} onClick={() => navigate("/inventory/purchaseOrder")}>
                        New Purchase Invoice
                    </button>
                    <button className={classes.btns} onClick={() => navigate("/customer-sales")}>
                        All Invoices
                    </button>
                    <button className={`${classes.btns} ${classes.greens}`} onClick={() => navigate("/settings/product-services")}>
                        Product & Services
                    </button>
                </div>
                <div className={classes.act}>
                    <form className={classes.form}>
                        <div className={classes.group}>
                            <label>Period:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>Current month</option>
                                    <option>Last month</option>
                                    <option>This year</option>
                                    <option>Custom range</option>
                                </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div>
                        </div> 
                        <div className={classes.group1}>
                            <label>Group by:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>None</option> <option>Date</option>
                                    <option>Transaction Type</option>
                                    <option>Amount</option>
                                </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div> </div> <div className={classes.group1}>
                            <label>Start Date:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>01/01/25</option> <option>01/12/24</option>
                                    <option>01/11/24</option> <option>01/10/24</option>
                                </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div>
                        </div>
                        <div className={classes.group1}>
                            <label>End Date:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>{new Date().toLocaleDateString('en-GB')}</option>
                                    <option>31/12/24</option> <option>30/11/24</option>
                                    <option>31/10/24</option> </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div>
                        </div>
                        <div className={classes.group1}>
                            <button className={classes.greenBtn}
                                type="button"> <BarChart2 size={16} style={{ marginRight: "6px" }}
                                />
                                Run Report
                            </button>
                        </div>
                    </form>
                    <div className={classes.iconbarr}>
                        <div className={classes.iconbar}>
                            <Filter size={16} style={{ marginRight: "6px" }}
                            />
                            Filter
                        </div>
                        <div className={classes.iconbar}>
                            <CalendarClock size={16} style={{ marginRight: "6px" }} />
                            Schedule
                        </div>
                    </div>
                </div>
                <div className={classes.matron}> <div>
                    <button className={classes.backButton}
                        onClick={() => navigate(-1)}> <ArrowLeft size={16} style={{ marginRight: '6px' }}
                        />
                    </button>
                </div>
                    <div>
                        <p className={classes.toplea}>Payment Receipt</p>
                        <p className={classes.topl}>Payment as of {new Date().toLocaleDateString()}</p>
                    </div>
                </div>

                {/* replaced header and table container with A4 receipt JSX (namespaced classes pr_*) */}
                <div className={classes.containere}>
                    <div className={classes.iconbars}>
                        <div className={classes.iconbarss}>
                            <div className={classes.iconbar} onClick={handleMail} style={{ cursor: "pointer" }}>
                                <Mail size={16} />
                            </div>
                            <div className={classes.iconbar} onClick={handlePrinter} style={{ cursor: "pointer" }}>
                                <Printer size={16} />
                            </div>
                            <div className={classes.iconbar} onClick={handleDownload} style={{ cursor: "pointer" }}>
                                <Download size={16} />
                            </div>
                            <div className={classes.iconbar} onClick={handleSettings} style={{ cursor: "pointer" }}>
                                <Settings size={16} />
                            </div>

                        </div>
                    </div>
                    {/* replaced static header with dynamic companyInfo */}
                    <header className={classes.pr_header}>
                        <div className={classes.pr_logo}>
                            {/* show company logo if available else fallback image path */}
                            <img
                                src={companyInfo.logo || "/company-logo.png"}
                                alt={`${companyInfo.name || "Company"} Logo`}
                                className={classes.pr_logoImg}
                            />
                        </div>
                        <div className={classes.pr_companyInfo}>
                            <h1 className={classes.pr_companyName}>{companyInfo.name || "Company Name"}</h1>
                            <p className={classes.pr_companyAddr}>{companyInfo.address || "Company address not provided"}</p>
                            <p className={classes.pr_companyContact}>
                                {companyInfo.email ? `Email: ${companyInfo.email}` : "Email: N/A"}
                                {companyInfo.phone ? ` | Tel: ${companyInfo.phone}` : ""}
                            </p>
                        </div>
                    </header>

                    <div className={classes.pr_receiptTitle}>Official Payment Receipt</div>

                    <div className={classes.pr_receiptMeta}>
                        <table className={classes.pr_metaTable}>
                            <tbody>
                                <tr>
                                    <th className={classes.pr_metaLabel}>Receipt No:</th>
                                    <td className={classes.pr_metaValue}>{receiptNo}</td>
                                </tr>
                                <tr>
                                    <th className={classes.pr_metaLabel}>Date:</th>
                                    <td className={classes.pr_metaValue}>{formatDate(receiptDate)}</td>
                                </tr>
                                <tr>
                                    <th className={classes.pr_metaLabel}>Received From:</th>
                                    <td className={classes.pr_metaValue}>{receivedFrom}</td>
                                </tr>
                                <tr>
                                    <th className={classes.pr_metaLabel}>Payment Method:</th>
                                    <td className={classes.pr_metaValue}>{paymentMethod}</td>
                                </tr>
                                <tr>
                                    <th className={classes.pr_metaLabel}>Reference No:</th>
                                    <td className={classes.pr_metaValue}>{referenceNo}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <table className={classes.pr_itemsTable}>
                        <thead>
                            <tr className={classes.pr_itemsHeadRow}>
                                <th className={classes.pr_itemsHeader}>Description</th>
                                <th className={classes.pr_itemsHeader}>Amount ({displayCurrencyCode})</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className={classes.pr_itemsRow}>
                                <td className={classes.pr_itemDesc}>
                                    {payment?.description || payment?.narration || payment?.payment_description || "Payment"}
                                </td>
                                <td className={classes.pr_itemAmount}>
                                    {displayCurrencySymbol}{formatCurrency(lineAmountValue)}
                                </td>
                            </tr>
                            {/* If you have multiple line items in future, map them here */}
                        </tbody>
                    </table>

                    <div className={classes.pr_totalSection}>
                        <table className={classes.pr_totalTable}>
                            <tbody>
                                <tr>
                                    <td className={classes.pr_totalLabel}>Subtotal:</td>
                                    <td className={classes.pr_totalAmount}>{displayCurrencySymbol}{formatCurrency(subtotalValue)}</td>
                                </tr>
                                <tr>
                                    <td className={classes.pr_totalLabel}>Tax:</td>
                                    <td className={classes.pr_totalAmount}>{displayCurrencySymbol}{formatCurrency(taxValue)}</td>
                                </tr>
                                <tr>
                                    <td className={classes.pr_totalLabel}>Total Amount:</td>
                                    <td className={classes.pr_totalAmount}><strong>{displayCurrencySymbol}{formatCurrency(totalValue)}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className={classes.pr_amountWords}>
                        Amount in Words: <strong>{amountInWords || "N/A"}</strong>
                    </div>

                    <div className={classes.pr_signatureSection}>
                        <div className={classes.pr_signBox}>Prepared By<br /><small className={classes.pr_signDate}>Date:</small></div>
                        <div className={classes.pr_signBox}>Checked By<br /><small className={classes.pr_signDate}>Date:</small></div>
                        <div className={classes.pr_signBox}>Approved By<br /><small className={classes.pr_signDate}>Date:</small></div>
                    </div>

                    <footer className={classes.pr_footer}>
                        <p className={classes.pr_thanks}><strong>Thank you for your payment!</strong></p>
                        <p className={classes.pr_notice}>This receipt is system-generated and valid without signature or seal.</p>
                        <p className={classes.pr_copyright}>© 2025 Brookes Professional Services Limited</p>
                    </footer>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default PaymentReceipt;
